import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { Controller, useForm } from 'react-hook-form';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import '../../styles/modals/StudentInfoModal.css';
import Loading from '../Loading';
import TagInput from './TagInput';
import { createPrivilegedAccessEntry, updatePrivilegedAccessEntry, getSubscriptionCatalog } from '../../server/server';
import { notify } from '../../utils/util';

interface FormValues {
	emails: string[];
	domains: string[];
	subscriptionId: string;
	subscriptionOfferId: string;
	active: boolean;
}

const validateEmail = (value: string, existingTags: string[]): string | null => {
	if (existingTags.includes(value)) return 'This email already exists';
	if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
	return null;
};

const validateDomain = (value: string, existingTags: string[]): string | null => {
	if (existingTags.includes(value)) return 'This domain already exists';
	if (value.includes('@')) return 'Domain should not contain @';
	if (!value.includes('.')) return 'Invalid domain format (e.g., company.com)';
	if (value.startsWith('.') || value.endsWith('.')) return 'Invalid domain format';
	return null;
};

function CreateEditPrivilegedAccessModal({
	modalIsOpen,
	closeModal,
	entry,
	onSuccess,
}: {
	modalIsOpen: boolean;
	closeModal: () => void;
	entry: any | null;
	onSuccess: () => void;
}) {
	const [processing, setProcessing] = useState(false);
	const [subscriptionCatalog, setSubscriptionCatalog] = useState<any[]>([]);
	const [catalogLoading, setCatalogLoading] = useState(false);

	const { handleSubmit, reset, control, watch, setValue, formState } = useForm<FormValues>({
		mode: 'onChange',
		defaultValues: {
			emails: [],
			domains: [],
			subscriptionId: '',
			subscriptionOfferId: '',
			active: true,
		},
	});

	const selectedSubscriptionId = watch('subscriptionId');

	const selectedSubscription = subscriptionCatalog.find((sub: any) => sub.id === selectedSubscriptionId);
	const offers = selectedSubscription?.offers || [];

	useEffect(() => {
		const element = document.getElementById('home');
		ReactModal.setAppElement('#root');
		if (modalIsOpen) {
			if (element) element.style.filter = 'blur(5px)';
			loadCatalog();
		} else {
			if (element) element.style.filter = 'unset';
		}
	}, [modalIsOpen]);

	const loadCatalog = async () => {
		setCatalogLoading(true);
		const resp = await getSubscriptionCatalog();
		if (resp.status === 200) {
			const catalog = Array.isArray(resp.data) ? resp.data : [];
			setSubscriptionCatalog(catalog);

			if (entry) {
				setValue('emails', entry.emails || []);
				setValue('domains', entry.domains || []);
				setValue('subscriptionId', entry.subscriptionId || '');
				setValue('active', entry.active ?? true);
				// Set offer after catalog is loaded so the dropdown has options
				setTimeout(() => {
					setValue('subscriptionOfferId', entry.subscriptionOfferId || '');
				}, 0);
			}
		}
		setCatalogLoading(false);
	};

	// Reset offer when subscription changes (only in interactive usage, not initial population)
	const prevSubRef = React.useRef(selectedSubscriptionId);
	useEffect(() => {
		if (prevSubRef.current && prevSubRef.current !== selectedSubscriptionId) {
			setValue('subscriptionOfferId', '');
		}
		prevSubRef.current = selectedSubscriptionId;
	}, [selectedSubscriptionId, setValue]);

	const onSubmit = async (data: FormValues) => {
		if (data.emails.length === 0 && data.domains.length === 0) {
			notify('At least one email or domain is required');
			return;
		}
		if (!data.subscriptionId) {
			notify('Please select a subscription');
			return;
		}
		if (!data.subscriptionOfferId) {
			notify('Please select a subscription offer');
			return;
		}

		setProcessing(true);
		try {
			const body = {
				emails: data.emails,
				domains: data.domains,
				subscriptionId: data.subscriptionId,
				subscriptionOfferId: data.subscriptionOfferId,
				active: data.active,
			};

			let resp;
			if (entry?.id) {
				resp = await updatePrivilegedAccessEntry(entry.id, body);
			} else {
				resp = await createPrivilegedAccessEntry(body);
			}

			if (resp.status === 200 || resp.status === 201) {
				notify(entry?.id ? 'Entry updated successfully' : 'Entry created successfully');
				onSuccess();
				closeModal();
			} else {
				const errorMsg = resp.data?.message || 'An error occurred';
				notify(errorMsg);
			}
		} catch (error) {
			notify('An unexpected error occurred');
		} finally {
			setProcessing(false);
		}
	};

	const cleanUpFunction = () => {
		reset();
		setSubscriptionCatalog([]);
	};

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterOpen={() => {}}
			onAfterClose={() => cleanUpFunction()}
			onRequestClose={closeModal}
			style={{
				overlay: {
					zIndex: 7,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 900,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal'>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={() => closeModal()}>
						<CloseModalIcon />
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center'>
						<div className='flex-1'>
							<p className='text-primaryViolet font-semibold text-3xl text-left'>
								{entry ? 'Edit privileged access' : 'Create privileged access'}
							</p>
						</div>
					</div>

					{/* Emails */}
					<div className='mt-3'>
						<Controller
							control={control}
							name='emails'
							render={({ field }) => (
								<TagInput
									tags={field.value}
									onAddTag={(tag) => field.onChange([...field.value, tag])}
									onRemoveTag={(index) => field.onChange(field.value.filter((_: string, i: number) => i !== index))}
									placeholder='Type email and press Enter'
									validate={validateEmail}
									label='Emails'
								/>
							)}
						/>
					</div>

					{/* Domains */}
					<div className='mt-3'>
						<Controller
							control={control}
							name='domains'
							render={({ field }) => (
								<TagInput
									tags={field.value}
									onAddTag={(tag) => field.onChange([...field.value, tag])}
									onRemoveTag={(index) => field.onChange(field.value.filter((_: string, i: number) => i !== index))}
									placeholder='Type domain and press Enter (e.g., company.com)'
									validate={validateDomain}
									label='Domains'
								/>
							)}
						/>
					</div>

					{/* Subscription */}
					<div className='mt-3'>
						<p className='text-primaryViolet font-semibold text-lg text-left'>Subscription</p>
						<div className='border-b border-b-deselectPurple p-2 mt-2'>
							{catalogLoading ? (
								<div className='flex items-center gap-2'>
									<Loading size={16} primaryColor='#5E639B' stroke={5} />
									<span className='text-mudPurple text-[14px]'>Loading subscriptions...</span>
								</div>
							) : (
								<Controller
									control={control}
									name='subscriptionId'
									render={({ field }) => (
										<select
											value={field.value}
											onChange={(e) => field.onChange(e.target.value)}
											className='w-full text-primaryViolet bg-transparent outline-none cursor-pointer'
										>
											<option value=''>Select a subscription</option>
											{subscriptionCatalog.map((sub: any) => (
												<option key={sub.id} value={sub.id}>
													{sub.codeName || sub.name}
												</option>
											))}
										</select>
									)}
								/>
							)}
						</div>
					</div>

					{/* Subscription Offer */}
					<div className='mt-3'>
						<p className='text-primaryViolet font-semibold text-lg text-left'>Subscription Offer</p>
						<div className='border-b border-b-deselectPurple p-2 mt-2'>
							<Controller
								control={control}
								name='subscriptionOfferId'
								render={({ field }) => (
									<select
										value={field.value}
										onChange={(e) => field.onChange(e.target.value)}
										className='w-full text-primaryViolet bg-transparent outline-none cursor-pointer disabled:text-mudPurple disabled:cursor-not-allowed'
										disabled={!selectedSubscriptionId || offers.length === 0}
									>
										<option value=''>
											{!selectedSubscriptionId ? 'Select a subscription first' : offers.length === 0 ? 'No offers available' : 'Select an offer'}
										</option>
										{offers.map((offer: any) => (
											<option key={offer.id} value={offer.id}>
												{offer.label || offer.offerLabel || offer.codeName} — {offer.periodDuration} {offer.periodFrame}
											</option>
										))}
									</select>
								)}
							/>
						</div>
					</div>

					{/* Active Toggle */}
					<div className='mt-3 flex flex-row items-center gap-3'>
						<Controller
							control={control}
							name='active'
							render={({ field }) => (
								<div
									className='border-[1.5px] border-primaryViolet rounded-full h-[18px] w-[18px] flex items-center justify-center cursor-pointer'
									onClick={() => field.onChange(!field.value)}
								>
									{field.value && <div className='rounded-full h-[10px] w-[10px] bg-primaryViolet'></div>}
								</div>
							)}
						/>
						<label className='text-primaryViolet text-[16px] font-medium cursor-pointer not-selectable'>Active</label>
					</div>

					{/* Submit Button */}
					<div className='flex flex-row justify-end w-full gap-5 mt-2'>
						<button
							type='submit'
							className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-primaryViolet min-h-[60px] button-save-class transition'
							disabled={processing}
						>
							{processing ? (
								<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
							) : (
								<div className='flex flex-row items-center justify-center'>
									<p className='text-[18px] leading-5 font-semibold mr-3'>Save</p>
									<Save />
								</div>
							)}
						</button>
					</div>
				</form>
			</div>
		</ReactModal>
	);
}

export default CreateEditPrivilegedAccessModal;
